import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Check, X, ChevronRight, ClipboardCheck, BookOpen, PlayCircle } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg, useApi, fmtDT, submitOrQueue, useQueue } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Pill, Empty, Field } from "@/components/kit";
import { PhotoPicker, Photos } from "@/components/PhotoPicker";
import { StaffInput, StaffSummary, staffComplete, staffFails, useRoster } from "@/components/StaffFitness";

function itemFails(item, v, staffIncident = true) {
  if (item.kind === "staff") return staffIncident && staffFails(v);
  if (item.kind === "tick") return v === "no";
  if (item.kind === "temp" && v !== "" && v != null) {
    const n = Number(v);
    return (item.min != null && n < item.min) || (item.max != null && n > item.max);
  }
  return false;
}

function ItemInput({ item, idx, value, onChange }) {
  if (item.kind === "staff") return <StaffInput idx={idx} value={value} onChange={onChange} />;
  if (item.kind === "tick") {
    return (
      <div className="flex gap-2">
        {[["yes", Check, "bg-emerald-600 text-white border-emerald-600"], ["no", X, "bg-rose-600 text-white border-rose-600"]].map(([v, I, on]) => (
          <button key={v} type="button" data-testid={`check-item-${idx}-${v}`} onClick={() => onChange(v)}
            className={`ka-press flex h-12 flex-1 items-center justify-center gap-1.5 rounded-xl border text-sm font-semibold capitalize ${value === v ? on : "border-slate-300 bg-white text-slate-700 hover:border-slate-500"}`}>
            <I className="h-4 w-4" />{v}
          </button>
        ))}
      </div>
    );
  }
  if (item.kind === "temp") {
    const lim = [item.min != null && `min ${item.min}°C`, item.max != null && `max ${item.max}°C`].filter(Boolean).join(", ");
    return <div className="flex items-center gap-2"><input data-testid={`check-item-${idx}-temp`} type="number" step="0.1" inputMode="decimal" className="ka-input h-12 text-lg font-semibold" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="°C" /><span className="whitespace-nowrap text-xs text-slate-500">{lim}</span></div>;
  }
  return <textarea data-testid={`check-item-${idx}-text`} rows={2} className="ka-input" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

function HowTo({ item, idx }) {
  const [open, setOpen] = useState(false);
  if (!item.instruction && !item.photo_id && !item.video_url) return null;
  return (
    <div className="-mt-1 mb-3">
      <button type="button" data-testid={`check-item-${idx}-howto`} onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 text-xs font-semibold text-sky-700 hover:underline"><BookOpen className="h-3.5 w-3.5" />{open ? "Hide how-to" : "How to do this"}</button>
      {open && (
        <div data-testid={`check-item-${idx}-howto-panel`} className="mt-2 space-y-2 rounded-lg bg-sky-50 p-3 text-sm text-sky-950">
          {item.instruction && <p className="whitespace-pre-line">{item.instruction}</p>}
          {item.photo_id && <Photos ids={[item.photo_id]} size="h-32 w-32" />}
          {item.video_url && <a href={item.video_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 font-semibold text-sky-800 underline"><PlayCircle className="h-4 w-4" />Watch the video</a>}
        </div>
      )}
    </div>
  );
}

function RunSheet({ entry, onClose, onDone }) {
  const t = entry.template;
  const { user, business } = useAuth();
  const roster = useRoster();
  const staffIncident = business?.staff_issue_incident !== false;
  const [vals, setVals] = useState(t.items.map((it) => (it.kind === "staff" ? [] : "")));
  const [corrective, setCorrective] = useState("");
  const [notes, setNotes] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const failing = t.items.some((it, i) => itemFails(it, vals[i], staffIncident));
  const staffMissing = t.items.some((it, i) => it.kind === "staff" && !staffComplete(vals[i], roster));

  const submit = async () => {
    if (staffMissing) return toast.error("Mark every team member, and for any issue say what it is and whether it's fixed");
    setBusy(true);
    try {
      const res = await submitOrQueue("/checks/runs", { template_id: t.id, answers: vals.map((v) => ({ value: Array.isArray(v) ? JSON.stringify(v) : String(v) })), notes, corrective_action: corrective, photo_ids: photos, completed_at: new Date().toISOString() },
        { user_id: user.id, kind: "check", label: t.name, template_id: t.id, venue_id: t.venue_id });
      if (res.queued) toast.info("Saved on this device — it will sync when you're back online");
      else toast.success(failing ? "Saved — an incident was raised for the failed items" : "Check signed off");
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-6 space-y-5">
      {t.items.map((it, i) => (
        <div key={i} className={`rounded-xl border p-4 ${itemFails(it, vals[i], staffIncident) ? "border-rose-300 bg-rose-50" : "border-slate-200"}`}>
          <div className="mb-3 text-sm font-medium text-slate-800">{it.label}</div>
          <HowTo item={it} idx={i} />
          <ItemInput item={it} idx={i} value={vals[i]} onChange={(v) => setVals(vals.map((x, j) => (j === i ? v : x)))} />
        </div>
      ))}
      {failing && (
        <Field label="Corrective action taken *" hint="What did you do about it? e.g. moved food to another fridge, re-cleaned, told the manager.">
          <textarea data-testid="check-corrective-input" rows={3} className="ka-input border-amber-400" value={corrective} onChange={(e) => setCorrective(e.target.value)} />
        </Field>
      )}
      <Field label="Notes"><textarea data-testid="check-notes-input" rows={2} className="ka-input" value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
      <Field label="Photo evidence" hint="e.g. a clean prep area, a thermometer reading or a problem you found"><PhotoPicker testid="check-photo" allowOffline value={photos} onChange={setPhotos} /></Field>
      <div className="flex gap-2">
        <button onClick={onClose} className="ka-press flex-1 rounded-full border border-slate-300 py-3 text-sm font-semibold">Cancel</button>
        <button data-testid="submit-check-button" disabled={busy} onClick={submit} className="ka-press flex-[2] rounded-full bg-emerald-600 py-3 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60">{busy ? "Saving…" : "Sign off check"}</button>
      </div>
    </div>
  );
}

function RunSummary({ run }) {
  return (
    <div className="mt-6 space-y-3">
      <div className="text-sm text-slate-600">Signed off by <b>{run.completed_by}</b> · {fmtDT(run.completed_at)}</div>
      {run.answers.map((a, i) => a.kind === "staff" ? <StaffSummary key={i} answer={a} /> : (
        <div key={i} className={`flex justify-between gap-3 rounded-lg border px-3 py-2 text-sm ${a.ok ? "border-slate-200" : "border-rose-300 bg-rose-50"}`}><span>{a.label}</span><b className="capitalize">{a.kind === "temp" ? `${a.value}°C` : a.value || "—"}</b></div>
      ))}
      {run.corrective_action && <div className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900"><b>Corrective action:</b> {run.corrective_action}</div>}
      {run.notes && <div className="text-sm text-slate-600"><b>Notes:</b> {run.notes}</div>}
      <Photos ids={run.photo_ids} />
    </div>
  );
}

export default function Checks() {
  const { venueId, venue, can, user } = useAuth();
  const { data, reload } = useApi(venueId ? "/checks/today" : null, { venue_id: venueId });
  const { data: closed } = useApi(venueId ? "/checks/closed-today" : null, { venue_id: venueId });
  const [open, setOpen] = useState(null);
  const [params, setParams] = useSearchParams();
  const deepId = params.get("template");
  useEffect(() => {
    if (!deepId || !data) return;
    const hit = data.find((e) => e.template.id === deepId);
    if (hit) setOpen(hit);
    else {
      toast.error("That check isn't scheduled today", { id: "deeplink-missing" });
      setParams({}, { replace: true });
    }
  }, [deepId, data, setParams]);
  const closeSheet = () => {
    setOpen(null);
    if (deepId) setParams({}, { replace: true });
  };
  const queue = useQueue();
  const pendingIds = new Set(queue.filter((i) => i.kind === "check" && i.user_id === user.id).map((i) => i.template_id));
  const rows = (data || []).map((e) => (!e.run && pendingIds.has(e.template.id) ? { ...e, status: "pending_sync" } : e));
  const groups = rows.reduce((acc, e) => ({ ...acc, [e.template.category]: [...(acc[e.template.category] || []), e] }), {});

  return (
    <div>
      <PageHeader eyebrow={venue?.name} title="Today's checks" subtitle="Tap a check to complete and sign it off. Failed items need a corrective action." />
      {data && !data.length && (closed?.closed_reason
        ? <Empty testid="checks-closed-today" icon={ClipboardCheck} title="Closed today" text={`${closed.closed_reason}. No checks are due today.`} />
        : <Empty testid="checks-empty" icon={ClipboardCheck} title="No checks scheduled" text="A manager can set up checklists in Checklists setup." />)}
      <div className="space-y-8">
        {Object.entries(groups).map(([cat, list]) => (
          <div key={cat}>
            <div className="ka-eyebrow mb-3">{cat}</div>
            <div className="grid gap-3 sm:grid-cols-2">
              {list.map((e) => (
                <button key={e.template.id} data-testid={`check-card-${e.template.id}`} onClick={() => setOpen(e)}
                  className={`ka-press ka-card flex items-center gap-4 p-5 text-left hover:border-slate-400 ${e.status === "overdue" ? "border-rose-300" : ""}`}>
                  <div className="flex-1">
                    <div className="font-display text-lg font-bold text-slate-900">{e.template.name}</div>
                    <div className="mt-1 text-xs text-slate-500">Due {e.template.due_time} · {e.template.items.some((it) => it.kind === "staff") ? "Every chef and staff member" : `${e.template.items.length} items`}</div>
                  </div>
                  <Pill status={e.status} testid={`check-status-${e.template.id}`} />
                  {e.run?.answers?.some((a) => a.kind === "staff" && a.staff?.some((s) => s.status === "issue" && !s.fixed)) && <span data-testid={`check-staff-issue-${e.template.id}`} className="rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-800">staff issue</span>}
                  <ChevronRight className="h-4 w-4 text-slate-400" />
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>
      <Sheet open={!!open} onOpenChange={(o) => !o && closeSheet()}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-lg">
          <SheetHeader><SheetTitle className="font-display text-2xl">{open?.template.name}</SheetTitle></SheetHeader>
          {open && (open.run ? <RunSummary run={open.run} /> : open.status === "pending_sync" ? <p data-testid="check-pending-sync" className="mt-6 rounded-xl bg-sky-50 p-4 text-sm text-sky-900">Signed off on this device. It will upload automatically when you're back online.</p> : can("record") ? <RunSheet entry={open} onClose={closeSheet} onDone={() => { closeSheet(); reload(); }} /> : <p className="mt-6 text-sm text-slate-500">Not completed yet. Your role is read-only.</p>)}
        </SheetContent>
      </Sheet>
    </div>
  );
}
